import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE } from "@/lib/api";
import { Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setSubmitted(true);
    } catch { /* no-op — we always succeed to prevent enumeration */
      setSubmitted(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#183D36] mb-6" data-testid="forgot-back-to-login">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
              <Sparkles size={16} />
            </span>
            <h1 className="font-heading text-2xl text-[#183D36]">Reset your password</h1>
          </div>
          {!submitted ? (
            <>
              <p className="text-sm text-[#4A4A4A] mb-6">Enter the email you use to sign in. If it&apos;s on our system, we&apos;ll send a reset link.</p>
              <form onSubmit={submit} className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    data-testid="forgot-input-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-submit"
                  className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
                >
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success">
              <div className="w-14 h-14 rounded-2xl bg-[#A8BFA3] flex items-center justify-center mb-3">
                <CheckCircle2 className="text-[#183D36]" size={22} />
              </div>
              <p className="text-[#252525] font-medium mb-2">If we found an account for <span className="text-[#183D36]">{email}</span>, a reset link is on its way.</p>
              <p className="text-sm text-[#4A4A4A]">Check your inbox — the link expires in 1 hour. Peek in spam if you don&apos;t see it in a couple of minutes.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
